import React from 'react';
import { Terminal, Activity, Zap } from 'lucide-react';
import { theme } from '../../styles/theme';

interface ServiceViewProps {
  serviceId: string;
  serviceName: string;
  darkMode: boolean;
}

const ServiceView: React.FC<ServiceViewProps> = ({ serviceName, darkMode }) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ 
          color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
        }}>
          {serviceName}
        </h1>
        <div className="flex space-x-3">
          <button className="px-4 py-2 rounded-lg transition-colors flex items-center space-x-2" style={{
            backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
            border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
            color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.primary,
          }}>
            <Terminal className="w-4 h-4" />
            <span>Logs</span>
          </button>
          <button className="px-4 py-2 rounded-lg transition-colors flex items-center space-x-2" style={{
            backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
            border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
            color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.primary,
          }}>
            <Activity className="w-4 h-4" />
            <span>Metrics</span>
          </button>
          <button className="px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 text-white" style={{
            backgroundColor: theme.colors.primary.main,
          }}>
            <Zap className="w-4 h-4" />
            <span>Restart</span>
          </button>
        </div>
      </div>
      
      {/* Service Status Card */}
      <div className="rounded-xl p-6" style={{
        backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
        boxShadow: '0 4px 20px rgba(228, 165, 128, 0.1)',
      }}>
        <div className="grid grid-cols-4 gap-6">
          <div>
            <p className="text-sm mb-1" style={{ color: theme.colors.text.secondary }}>Status</p>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full animate-pulse" style={{ 
                backgroundColor: theme.colors.status.success 
              }}></div>
              <span className="font-semibold" style={{ 
                color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
              }}>
                Healthy
              </span>
            </div>
          </div>
          <div>
            <p className="text-sm mb-1" style={{ color: theme.colors.text.secondary }}>Uptime</p>
            <p className="font-semibold" style={{ 
              color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
            }}>
              99.99%
            </p>
          </div>
          <div>
            <p className="text-sm mb-1" style={{ color: theme.colors.text.secondary }}>Response Time</p>
            <p className="font-semibold" style={{ 
              color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
            }}>
              32ms
            </p>
          </div>
          <div>
            <p className="text-sm mb-1" style={{ color: theme.colors.text.secondary }}>Requests/min</p>
            <p className="font-semibold" style={{ 
              color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
            }}>
              1,245
            </p>
          </div>
        </div>
      </div>

      {/* Additional Service Details */}
      <div className="grid grid-cols-2 gap-6">
        <div className="rounded-xl p-6" style={{
          backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
          boxShadow: '0 2px 10px rgba(228, 165, 128, 0.1)',
        }}>
          <h3 className="text-lg font-semibold mb-4" style={{
            color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary
          }}>
            Recent Deployments
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm" style={{ color: theme.colors.text.secondary }}>
                Version 1.2.3
              </span>
              <span className="text-xs" style={{ color: theme.colors.status.success }}>
                2 hours ago
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm" style={{ color: theme.colors.text.secondary }}>
                Version 1.2.2
              </span>
              <span className="text-xs" style={{ color: theme.colors.text.secondary }}>
                1 day ago
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm" style={{ color: theme.colors.text.secondary }}>
                Version 1.2.1
              </span>
              <span className="text-xs" style={{ color: theme.colors.text.secondary }}>
                3 days ago
              </span>
            </div>
          </div>
        </div>

        <div className="rounded-xl p-6" style={{
          backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
          boxShadow: '0 2px 10px rgba(228, 165, 128, 0.1)',
        }}>
          <h3 className="text-lg font-semibold mb-4" style={{
            color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary
          }}>
            Resource Usage
          </h3>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm" style={{ color: theme.colors.text.secondary }}>CPU</span>
                <span className="text-sm font-medium" style={{ 
                  color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
                }}>
                  45%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="h-2 rounded-full" style={{ 
                  width: '45%',
                  backgroundColor: theme.colors.status.success 
                }}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm" style={{ color: theme.colors.text.secondary }}>Memory</span>
                <span className="text-sm font-medium" style={{ 
                  color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
                }}>
                  62%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="h-2 rounded-full" style={{ 
                  width: '62%',
                  backgroundColor: theme.colors.status.warning 
                }}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm" style={{ color: theme.colors.text.secondary }}>Disk</span>
                <span className="text-sm font-medium" style={{ 
                  color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
                }}>
                  28%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="h-2 rounded-full" style={{ 
                  width: '28%',
                  backgroundColor: theme.colors.status.success 
                }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceView;